import { useContext } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { CloseIcon } from '../TipSheetIcons/close-icon';
import { Link } from 'react-router-dom';
import { formatClusterName } from '../Stories/Stories';
import { parseKeywords, formatKeywords } from '../../utils/keywordUtils';
import { getUserPreferences as getSavedUserPreferences } from '../../utils/userPreferences';
import { UserContext } from '../../hooks/UserContext.js';

const FilterChoicesOuter = styled.div`
  display: ${(props) => (props.variant === 'sidebar' ? 'none' : 'block')};
  border-bottom: 1px solid #e8ecef;

  @media (min-width: 769px) {
    display: ${(props) => (props.variant === 'mobile' || props.variant === 'mobile-dashboard' ? 'none' : 'block')};
  }

  @media (max-width: 768px) {
    display: ${(props) => (props.variant === 'sidebar' ? 'none' : 'block')};
    padding: ${(props) => (props.variant === 'mobile-dashboard' ? '1rem' : '0 0 1rem')};
    z-index: initial;
    position: relative;
    top: initial;
    margin-block-end: ${(props) => (props.variant === 'mobile' ? '1rem' : '0')};
  }
`;

const ActiveFiltersCard = styled.div`
  background: #eeeeee;
  border-radius: 6px;
  padding: 0.875rem 1rem 1rem;
  box-sizing: border-box;
`;

const ActiveFiltersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;

  a {
    color: #0a819f;
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    color: #086a82;
    text-decoration: underline;
  }
`;

const ActiveFiltersTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #000000;
`;

const ClearAllButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #0a819f;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: #086a82;
  }

  &:focus {
    outline: 2px solid #0a819f;
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const HeaderDivider = styled.div`
  height: 1px;
  background: #d8d8d8;
  margin-bottom: 0.75rem;
`;

const FilterChoicesInternal = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  width: 100%;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    width: max-content;
    max-width: none;
    margin: initial;

    &::-webkit-scrollbar {
      display: none;
    }

    scrollbar-width: none;
    -ms-overflow-style: none;
  }
`;

const FilterPill = styled.div`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  border: 1px solid #d0d0d0;
  background: #ffffff;

  & svg {
    width: 0.75rem;
    height: 0.75rem;
  }
`;

const PillText = styled.span`
  font-size: 0.875rem;
  color: #666666;
  white-space: nowrap;
  font-style: ${(props) => (props.$isUnquoted ? 'italic' : 'normal')};
`;

const PillCloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  margin-left: 0.5rem;
  padding: 0.125rem;
  display: flex;
  align-items: center;
  color: #666666;

  &:hover {
    color: #000000;
  }

  &:focus {
    outline: 2px solid #0e4c60;
    outline-offset: 2px;
    border-radius: 50%;
  }
`;

const SaveFiltersFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  padding-top: 0.25rem;

  a {
    color: #0a819f;
    font-size: 0.875rem;
    font-weight: 500;
    text-decoration: underline;
    white-space: nowrap;

    &:hover {
      color: #086a82;
    }

    &:focus {
      outline: 2px solid #0a819f;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }
`;

const SaveFiltersIcon = styled.svg`
  flex-shrink: 0;
  color: #0a819f;
`;

const FilterChoices = ({
  tags = [],
  setTags,
  legislators = [],
  setLegislators,
  variant,
  selectedBills = [],
  setSelectedBills,
  keywords = '',
  setKeywords,
  setBillFilterActive,
  setFilteredStoriesByBill,
  setSearchActive,
  setSearchStories,
  searchInputRef,
  originalStories,
  coverageActive,
  chamberFilter = 'both',
  setChamberFilter,
}) => {
  const { user } = useContext(UserContext);
  const preferences = getSavedUserPreferences(user);

  const savedTags = preferences.tags;
  const savedLegislators = preferences.legislators;
  const savedKeywordArray = preferences.keywordsArray;

  const isSavedTag = (tag) => savedTags.includes(tag);
  const isSavedLegislator = (pid) => savedLegislators.some((legislator) => legislator.pid === pid);
  const isSavedKeyword = (keyword) => savedKeywordArray.includes(keyword);
  // Handle removing a tag
  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  // Handle removing a legislator
  const handleRemoveLegislator = (legislatorId) => {
    setLegislators(
      legislators.filter((legislator) => legislator.pid !== legislatorId)
    );
  };

  // Handle removing a bill
  const handleRemoveBill = (billValue) => {
    const updatedBills = selectedBills.filter(
      (bill) => bill.value !== billValue
    );
    setSelectedBills(updatedBills);

    // If no bills left, reset bill filtering
    if (updatedBills.length === 0) {
      setBillFilterActive(false);
      setFilteredStoriesByBill([]);
    }
  };

  const handleClearAll = () => {
    setTags([]);
    setLegislators([]);
    setSelectedBills([]);
    setKeywords('');
    if (setChamberFilter) {
      setChamberFilter('both');
    }
    setBillFilterActive(false);
    setFilteredStoriesByBill([]);
    setSearchActive(false);
    setSearchStories([]);
    if (searchInputRef?.current) {
      searchInputRef.current.value = '';
      try {
        const ev = new Event('input', { bubbles: true });
        searchInputRef.current.dispatchEvent(ev);
      } catch (e) {
        // ignore
      }
    }
  };

  // Handle removing keywords
  const handleRemoveKeyword = (keywordToRemove) => {
    const keywordsArray = parseKeywords(keywords);
    const updatedArray = keywordsArray.filter((keyword) => keyword !== keywordToRemove);
    const updatedKeywords = formatKeywords(updatedArray);

    setKeywords(updatedKeywords);

    if (updatedKeywords.trim()) {
      // If there are still keywords, perform a search with the updated list
      performSearch(updatedKeywords);
    } else {
      // If all keywords are removed, clear search results
      setSearchActive(false);
      setSearchStories([]);

      if (searchInputRef?.current) {
        searchInputRef.current.value = '';
      }
    }
  };

  const performSearch = async (searchTerm) => {
    try {
      const axiosT = axios.create({
        baseURL: `${process.env.REACT_APP_FEATURED_API}`,
        headers: {
          Authorization: `JWT ${localStorage.getItem('token')}`,
        },
      });

      const response = await axiosT.get(
        `search/?p=main&q=${encodeURIComponent(searchTerm).replaceAll(
          '%2B',
          '+'
        )}`
      );

      const searchResults = Object.entries(response.data.tipsheets)
        .map(([id, data]) => ({
          id,
          relevance: data.relevance,
        }))
        .sort((a, b) => b.relevance - a.relevance)
        .map(({ id }) => id);

      const matchedStories = searchResults
        .map((id) => originalStories.find((story) => story.id === id))
        .filter(Boolean);

      setSearchActive(true);
      setSearchStories(matchedStories);
    } catch (err) {
      console.log('Error fetching search data:', err);
    }
  };

  // Format legislator name
  const formatLegislatorName = (legislator) => {
    return `${legislator.first} ${legislator.last} (${legislator.party[0]})`;
  };

  // Extract keywords from the search input if available
  const keywordsArray = parseKeywords(keywords);

  const activeFilterCount =
    tags.length +
    legislators.length +
    selectedBills.length +
    keywordsArray.length +
    (chamberFilter !== 'both' ? 1 : 0);

  if (activeFilterCount === 0) return null;

  return (
    <FilterChoicesOuter variant={variant}>
      <ActiveFiltersCard>
        <ActiveFiltersHeader>
          <ActiveFiltersTitle>
            {coverageActive ? 'My Filters' : 'Active Filters'} ({activeFilterCount})
          </ActiveFiltersTitle>
          {coverageActive ? (
            <Link to="/profile">Edit</Link>
          ) : (
            <ClearAllButton
              type="button"
              onClick={handleClearAll}
              aria-label="Clear all filters"
            >
              Clear All
            </ClearAllButton>
          )}
        </ActiveFiltersHeader>
        <HeaderDivider aria-hidden="true" />
        <FilterChoicesInternal>
        {/* Topic Tags */}
        {tags.map((tag) => {
          const canRemoveTag = !coverageActive || !isSavedTag(tag);
          return (
            <FilterPill key={`tag-${tag}`}>
              <PillText>{formatClusterName(tag)}</PillText>
              {canRemoveTag && (
                <PillCloseButton
                  onClick={() => handleRemoveTag(tag)}
                  aria-label={`Remove ${tag} tag filter`}
                >
                  <CloseIcon />
                </PillCloseButton>
              )}
            </FilterPill>
          );
        })}

        {/* Legislators */}
        {legislators.map((legislator) => {
          const canRemoveLegislator = !coverageActive || !isSavedLegislator(legislator.pid);
          return (
            <FilterPill key={`legislator-${legislator.pid}`}>
              <PillText>{formatLegislatorName(legislator)}</PillText>
              {canRemoveLegislator && (
                <PillCloseButton
                  onClick={() => handleRemoveLegislator(legislator.pid)}
                  aria-label={`Remove ${legislator.first} ${legislator.last} filter`}
                >
                  <CloseIcon />
                </PillCloseButton>
              )}
            </FilterPill>
          );
        })}
        {/* Chamber filter pill */}
        {chamberFilter !== 'both' && (
          <FilterPill key="chamber-filter">
            <PillText>
              {chamberFilter === 'senate' ? 'Senate only' : 'Assembly only'}
            </PillText>
            {setChamberFilter && (
              <PillCloseButton
                onClick={() => setChamberFilter('both')}
                aria-label="Clear chamber filter"
              >
                <CloseIcon />
              </PillCloseButton>
            )}
          </FilterPill>
        )}

        {/* Bill Numbers */}
        {selectedBills.map((bill) => (
          <FilterPill key={`bill-${bill.value}`}>
            <PillText>{bill.label}</PillText>
            {setSelectedBills && (
              <PillCloseButton
                onClick={() => handleRemoveBill(bill.value)}
                aria-label={`Remove ${bill.value} bill filter`}
              >
                <CloseIcon />
              </PillCloseButton>
            )}
          </FilterPill>
        ))}

        {/* Keywords */}
        {keywordsArray.length > 0 &&
          keywordsArray.map((keyword, index) => {
            const isQuoted = /^".*"$/.test(keyword);
            const canRemoveKeyword = !coverageActive || !isSavedKeyword(keyword);
            return (
              <FilterPill key={`keyword-${keyword}-${index}`}>
                <PillText $isUnquoted={!isQuoted}>{keyword}</PillText>
                {canRemoveKeyword && (
                  <PillCloseButton
                    onClick={() => handleRemoveKeyword(keyword)}
                    aria-label={`Remove ${keyword} from search`}
                  >
                    <CloseIcon />
                  </PillCloseButton>
                )}
              </FilterPill>
            );
          })}
        </FilterChoicesInternal>
        {!coverageActive && false && (
          <SaveFiltersFooter>
            <SaveFiltersIcon
              width="18"
              height="18"
              viewBox="0 0 18 18"
              aria-hidden="true"
              focusable="false"
            >
              <rect
                x="3.25"
                y="2.75"
                width="11.5"
                height="12.5"
                rx="2"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.35"
              />
              <path
                fill="none"
                stroke="currentColor"
                strokeWidth="1.35"
                strokeLinecap="round"
                d="M9 7v4M7 9h4"
              />
            </SaveFiltersIcon>
            <Link to="/profile">Save filters</Link>
          </SaveFiltersFooter>
        )}
      </ActiveFiltersCard>
    </FilterChoicesOuter>
  );
};

export default FilterChoices;
